import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import MainLayout from '../../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Clock,
  Calendar,
  Plus,
  Trash2,
  Loader2,
  Save,
  RefreshCw,
  Lock,
  Repeat,
  CalendarOff
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/axios';
import { cn } from '../../lib/utils';

const DAYS = [
  { key: 'monday', label: 'Lundi' },
  { key: 'tuesday', label: 'Mardi' },
  { key: 'wednesday', label: 'Mercredi' },
  { key: 'thursday', label: 'Jeudi' },
  { key: 'friday', label: 'Vendredi' },
  { key: 'saturday', label: 'Samedi' },
  { key: 'sunday', label: 'Dimanche' }
];

const DEFAULT_DAY = {
  enabled: true,
  start: '09:00',
  break_start: '12:00',
  break_end: '14:00',
  afternoon_end: '18:00'
};

const MySchedulePage = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  
  const [workingHours, setWorkingHours] = useState({});
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Dialog for adding blocked slot
  const [showBlockDialog, setShowBlockDialog] = useState(false);
  const [blockForm, setBlockForm] = useState({
    is_recurring: false,
    recurring_day: 'monday',
    date: '',
    start_time: '09:00',
    end_time: '10:00',
    reason: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const [hoursRes, blocksRes] = await Promise.all([
        api.get('/calendar/my-working-hours'),
        api.get('/calendar/my-blocked-slots')
      ]);
      
      setWorkingHours(hoursRes.data || {});
      setBlockedSlots(blocksRes.data || []);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDayChange = (day, field, value) => {
    setWorkingHours(prev => ({
      ...prev,
      [day]: {
        ...(prev[day] || DEFAULT_DAY),
        [field]: value
      }
    }));
  };

  const handleSaveHours = async () => {
    setSaving(true);
    try {
      await api.put('/calendar/my-working-hours', workingHours);
      toast.success('Horaires enregistrés');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleAddBlockedSlot = async () => {
    try {
      await api.post('/calendar/my-blocked-slots', blockForm);
      toast.success('Créneau bloqué ajouté');
      setShowBlockDialog(false);
      setBlockForm({
        is_recurring: false,
        recurring_day: 'monday',
        date: '',
        start_time: '09:00',
        end_time: '10:00',
        reason: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleDeleteBlockedSlot = async (slotId) => {
    try {
      await api.delete(`/calendar/my-blocked-slots/${slotId}`);
      toast.success('Créneau supprimé');
      setBlockedSlots(prev => prev.filter(s => s.id !== slotId));
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const recurringSlots = blockedSlots.filter(s => s.is_recurring);
  const oneTimeSlots = blockedSlots.filter(s => !s.is_recurring);

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2" style={{ color: theme.colors?.primary }}>
              <Clock className="w-6 h-6" />
              Mes Horaires
            </h1>
            <p className="text-sm text-slate-500">Définissez vos disponibilités et bloquez des créneaux</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={fetchData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualiser
            </Button>
            <Button onClick={handleSaveHours} disabled={saving} style={{ backgroundColor: theme.colors?.accent }}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Enregistrer
            </Button>
          </div>
        </div>

        {/* Working Hours */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Horaires de travail
            </CardTitle>
            <CardDescription>
              Définissez vos horaires habituels pour chaque jour de la semaine
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {DAYS.map(({ key, label }) => {
                const dayConfig = workingHours[key] || DEFAULT_DAY;
                const isEnabled = dayConfig.enabled !== false;
                
                return (
                  <div 
                    key={key} 
                    className={cn(
                      "flex items-center gap-4 p-4 rounded-lg border transition-all",
                      isEnabled ? "bg-white" : "bg-slate-50 opacity-60"
                    )}
                  >
                    <div className="w-24">
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={isEnabled}
                          onCheckedChange={(checked) => handleDayChange(key, 'enabled', checked)}
                        />
                        <span className="font-medium">{label}</span>
                      </div>
                    </div>
                    
                    {isEnabled ? (
                      <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                          <Label className="text-xs text-slate-500">Matin début</Label>
                          <Input
                            type="time"
                            value={dayConfig.start || '09:00'}
                            onChange={(e) => handleDayChange(key, 'start', e.target.value)}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">Pause début</Label>
                          <Input
                            type="time"
                            value={dayConfig.break_start || '12:00'}
                            onChange={(e) => handleDayChange(key, 'break_start', e.target.value)}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">Pause fin</Label>
                          <Input
                            type="time"
                            value={dayConfig.break_end || '14:00'}
                            onChange={(e) => handleDayChange(key, 'break_end', e.target.value)}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">Fin journée</Label>
                          <Input
                            type="time"
                            value={dayConfig.afternoon_end || '18:00'}
                            onChange={(e) => handleDayChange(key, 'afternoon_end', e.target.value)}
                            className="mt-1"
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="flex-1 text-sm text-slate-500">
                        Non travaillé
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Blocked Slots */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Lock className="w-5 h-5" />
                  Créneaux bloqués
                </CardTitle>
                <CardDescription>
                  Bloquez des créneaux où vous n&apos;êtes pas disponible pour les RDV
                </CardDescription>
              </div>
              <Button onClick={() => setShowBlockDialog(true)} size="sm">
                <Plus className="w-4 h-4 mr-2" />
                Bloquer un créneau
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {blockedSlots.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <CalendarOff className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p>Aucun créneau bloqué</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Recurring slots */}
                {recurringSlots.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-slate-600 mb-2 flex items-center gap-2">
                      <Repeat className="w-4 h-4" />
                      Récurrents ({recurringSlots.length})
                    </h4>
                    <div className="space-y-2">
                      {recurringSlots.map((slot) => (
                        <div 
                          key={slot.id}
                          className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-200"
                        >
                          <div className="flex items-center gap-3">
                            <Badge className="bg-amber-100 text-amber-800">
                              {DAYS.find(d => d.key === slot.recurring_day)?.label || slot.recurring_day}
                            </Badge>
                            <span className="font-medium">
                              {slot.start_time} - {slot.end_time}
                            </span>
                            {slot.reason && (
                              <span className="text-sm text-slate-500">({slot.reason})</span>
                            )}
                          </div>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="text-red-500 hover:text-red-700"
                            onClick={() => handleDeleteBlockedSlot(slot.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* One-time slots */}
                {oneTimeSlots.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-slate-600 mb-2 flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      Ponctuels ({oneTimeSlots.length})
                    </h4>
                    <div className="space-y-2">
                      {oneTimeSlots.map((slot) => (
                        <div 
                          key={slot.id}
                          className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200"
                        >
                          <div className="flex items-center gap-3">
                            <Badge className="bg-blue-100 text-blue-800">
                              {new Date(slot.date).toLocaleDateString('fr-FR', { 
                                weekday: 'short', 
                                day: 'numeric', 
                                month: 'short' 
                              })}
                            </Badge>
                            <span className="font-medium">
                              {slot.start_time} - {slot.end_time}
                            </span>
                            {slot.reason && (
                              <span className="text-sm text-slate-500">({slot.reason})</span>
                            )}
                          </div>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="text-red-500 hover:text-red-700"
                            onClick={() => handleDeleteBlockedSlot(slot.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Add Blocked Slot Dialog */}
      <Dialog open={showBlockDialog} onOpenChange={setShowBlockDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5" />
              Bloquer un créneau
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Type */}
            <div className="flex items-center gap-4">
              <Label className="w-24">Type</Label>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={!blockForm.is_recurring}
                    onChange={() => setBlockForm(prev => ({ ...prev, is_recurring: false }))}
                  />
                  <span>Ponctuel</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={blockForm.is_recurring}
                    onChange={() => setBlockForm(prev => ({ ...prev, is_recurring: true }))}
                  />
                  <span>Récurrent</span>
                </label>
              </div>
            </div>
            
            {/* Date or Day */}
            {blockForm.is_recurring ? (
              <div>
                <Label>Jour de la semaine</Label>
                <Select
                  value={blockForm.recurring_day}
                  onValueChange={(value) => setBlockForm(prev => ({ ...prev, recurring_day: value }))}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DAYS.map(({ key, label }) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div>
                <Label>Date</Label>
                <Input
                  type="date"
                  value={blockForm.date}
                  onChange={(e) => setBlockForm(prev => ({ ...prev, date: e.target.value }))}
                  className="mt-1"
                />
              </div>
            )}
            
            {/* Time */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Heure début</Label>
                <Input
                  type="time"
                  value={blockForm.start_time}
                  onChange={(e) => setBlockForm(prev => ({ ...prev, start_time: e.target.value }))}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Heure fin</Label>
                <Input
                  type="time"
                  value={blockForm.end_time}
                  onChange={(e) => setBlockForm(prev => ({ ...prev, end_time: e.target.value }))}
                  className="mt-1"
                />
              </div>
            </div>
            
            {/* Reason */}
            <div>
              <Label>Motif (optionnel)</Label>
              <Input
                value={blockForm.reason}
                onChange={(e) => setBlockForm(prev => ({ ...prev, reason: e.target.value }))}
                placeholder="Ex: Formation, RDV médical..."
                className="mt-1"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBlockDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleAddBlockedSlot} style={{ backgroundColor: theme.colors?.accent }}>
              Bloquer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};

export default MySchedulePage;
